import React, { useState, useEffect, useCallback } from 'react';
import { 
  FileText, Upload, Eye, Trash2, Copy, Check, Globe, Lock, 
  RefreshCw, X, Replace
} from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogCancel,
} from '@/components/ui/alert-dialog';

const API_URL = window.__BACKEND_URL;

// Allowed link targets with labels
const LINK_TARGETS = [
  { value: 'footer', label: 'Footer' },
  { value: 'signup', label: 'Signup Page' },
  { value: 'login', label: 'Login Page' },
  { value: 'oauth', label: 'OAuth Consent' },
  { value: 'affiliate', label: 'Affiliate Pages' },
  { value: 'autopilot', label: 'Autopilot Consent' },
  { value: 'app_review', label: 'App Review' },
];

export default function LegalVault() {
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [showUploadForm, setShowUploadForm] = useState(false);
  const [copiedSlug, setCopiedSlug] = useState(null);
  
  // Replace modal state
  const [replaceModal, setReplaceModal] = useState({ open: false, doc: null });
  const [replaceFile, setReplaceFile] = useState(null);
  const [replacing, setReplacing] = useState(false);
  
  // Upload form state
  const [uploadForm, setUploadForm] = useState({
    title: '',
    description: '',
    visibility: 'public',
    link_targets: [],
    file: null
  });

  const fetchDocuments = useCallback(async () => {
    try {
      setLoading(true);
      const res = await fetch(`${API_URL}/api/admin/legal/vault`, {
        credentials: 'include'
      });
      if (res.ok) {
        const data = await res.json();
        setDocuments(data);
      } else {
        toast.error('Failed to load documents');
      }
    } catch (err) {
      console.error('Error fetching legal documents:', err);
      toast.error('Failed to load documents');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchDocuments();
  }, [fetchDocuments]);

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file && file.type === 'application/pdf') {
      setUploadForm(prev => ({ ...prev, file }));
    } else {
      toast.error('Please select a PDF file');
    }
  };

  const handleReplaceFileChange = (e) => {
    const file = e.target.files[0];
    if (file && file.type === 'application/pdf') {
      setReplaceFile(file);
    } else {
      toast.error('Please select a PDF file');
    }
  };

  const handleTargetToggle = (target) => {
    setUploadForm(prev => {
      const targets = prev.link_targets.includes(target)
        ? prev.link_targets.filter(t => t !== target)
        : [...prev.link_targets, target];
      return { ...prev, link_targets: targets };
    });
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    
    if (!uploadForm.title.trim()) {
      toast.error('Title is required');
      return;
    }
    if (!uploadForm.file) {
      toast.error('Please select a PDF file');
      return;
    }

    setUploading(true);
    
    try {
      const formData = new FormData();
      formData.append('title', uploadForm.title);
      formData.append('description', uploadForm.description);
      formData.append('visibility', uploadForm.visibility);
      formData.append('link_targets', uploadForm.link_targets.join(','));
      formData.append('file', uploadForm.file);

      const res = await fetch(`${API_URL}/api/admin/legal/vault`, {
        method: 'POST',
        credentials: 'include',
        body: formData
      });

      const data = await res.json();
      
      if (data.success) {
        toast.success('Document uploaded successfully!');
        if (data.public_url) {
          toast.info(`Public URL: ${window.location.origin}${data.public_url}`);
        }
        setShowUploadForm(false);
        setUploadForm({
          title: '',
          description: '',
          visibility: 'public',
          link_targets: [],
          file: null
        });
        fetchDocuments();
      } else {
        toast.error(data.error || 'Upload failed');
      }
    } catch (err) {
      console.error('Upload error:', err);
      toast.error('Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const handleReplace = async () => {
    if (!replaceFile || !replaceModal.doc) return;
    
    // Validate file size (max 50MB)
    const maxSize = 50 * 1024 * 1024;
    if (replaceFile.size > maxSize) {
      toast.error('File too large. Maximum size is 50MB.');
      return;
    }
    
    setReplacing(true);
    
    try {
      const formData = new FormData();
      formData.append('file', replaceFile);

      const res = await fetch(`${API_URL}/api/admin/legal/vault/${replaceModal.doc.slug}/replace-pdf`, {
        method: 'PUT',
        credentials: 'include',
        body: formData
      });

      if (!res.ok) {
        const errorText = await res.text();
        console.error('Replace response error:', res.status, errorText);
        toast.error(`Replace failed: ${res.status === 413 ? 'File too large' : res.statusText}`);
        return;
      }

      const data = await res.json();
      
      if (data.success) {
        toast.success('Document updated successfully!');
        setReplaceModal({ open: false, doc: null });
        setReplaceFile(null);
        fetchDocuments();
      } else {
        toast.error(data.error || data.detail || 'Replace failed');
      }
    } catch (err) {
      console.error('Replace error:', err);
      toast.error(`Replace failed: ${err.message}`);
    } finally {
      setReplacing(false);
    }
  };

  const handleDelete = async (slug) => {
    if (!confirm(`Delete document "${slug}"? This cannot be undone.`)) return;

    try {
      const res = await fetch(`${API_URL}/api/admin/legal/vault/${slug}`, {
        method: 'DELETE',
        credentials: 'include'
      });

      if (res.ok) {
        toast.success('Document deleted');
        fetchDocuments();
      } else {
        toast.error('Failed to delete document');
      }
    } catch (err) {
      toast.error('Failed to delete document');
    }
  };

  const copyUrl = (slug) => {
    const url = `${window.location.origin}/legal/${slug}`;
    navigator.clipboard.writeText(url);
    setCopiedSlug(slug);
    toast.success('URL copied to clipboard');
    setTimeout(() => setCopiedSlug(null), 2000);
  };

  const openPreview = (slug) => {
    window.open(`/legal/${slug}`, '_blank');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 animate-spin text-cyan-500" />
      </div>
    );
  }

  return (
    <div className="p-8 max-w-6xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold text-white flex items-center gap-3">
            <FileText className="w-8 h-8 text-cyan-500" />
            Legal Vault
          </h1>
          <p className="text-gray-400 mt-1">
            Single source of truth for all compliance documents
          </p>
        </div>
        <Button 
          onClick={() => setShowUploadForm(true)}
          className="bg-cyan-600 hover:bg-cyan-700"
          data-testid="upload-document-btn"
        >
          <Upload className="w-4 h-4 mr-2" />
          Upload Document
        </Button>
      </div>

      {/* Upload Form Modal */}
      {showUploadForm && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-lg bg-[#1a1d23] border-gray-800">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="text-white">Upload Legal Document</CardTitle>
                <Button 
                  variant="ghost" 
                  size="sm"
                  onClick={() => setShowUploadForm(false)}
                >
                  <X className="w-4 h-4" />
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleUpload} className="space-y-4">
                {/* Title */}
                <div>
                  <label className="block text-sm text-gray-400 mb-1">Title *</label>
                  <Input
                    value={uploadForm.title}
                    onChange={(e) => setUploadForm(prev => ({ ...prev, title: e.target.value }))}
                    placeholder="e.g., Privacy Policy"
                    className="bg-gray-800 border-gray-700"
                    data-testid="upload-title-input"
                  />
                </div>

                {/* Description */}
                <div>
                  <label className="block text-sm text-gray-400 mb-1">Description</label>
                  <Input
                    value={uploadForm.description}
                    onChange={(e) => setUploadForm(prev => ({ ...prev, description: e.target.value }))}
                    placeholder="Brief description..."
                    className="bg-gray-800 border-gray-700"
                  />
                </div>

                {/* Visibility */}
                <div>
                  <label className="block text-sm text-gray-400 mb-2">Visibility</label>
                  <div className="flex gap-3">
                    <Button
                      type="button"
                      variant={uploadForm.visibility === 'public' ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => setUploadForm(prev => ({ ...prev, visibility: 'public' }))}
                      className={uploadForm.visibility === 'public' ? 'bg-green-600' : ''}
                    >
                      <Globe className="w-4 h-4 mr-1" />
                      Public
                    </Button>
                    <Button
                      type="button"
                      variant={uploadForm.visibility === 'private' ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => setUploadForm(prev => ({ ...prev, visibility: 'private' }))}
                      className={uploadForm.visibility === 'private' ? 'bg-yellow-600' : ''}
                    >
                      <Lock className="w-4 h-4 mr-1" />
                      Private
                    </Button>
                  </div>
                </div>

                {/* Link Targets */}
                <div>
                  <label className="block text-sm text-gray-400 mb-2">Link Targets</label>
                  <div className="flex flex-wrap gap-2">
                    {LINK_TARGETS.map(target => (
                      <Badge
                        key={target.value}
                        variant={uploadForm.link_targets.includes(target.value) ? 'default' : 'outline'}
                        className={`cursor-pointer ${
                          uploadForm.link_targets.includes(target.value)
                            ? 'bg-cyan-600 hover:bg-cyan-700'
                            : 'hover:bg-gray-700'
                        }`}
                        onClick={() => handleTargetToggle(target.value)}
                      >
                        {target.label}
                      </Badge>
                    ))}
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    Select where this document should be linked automatically
                  </p>
                </div>

                {/* File Upload */}
                <div>
                  <label className="block text-sm text-gray-400 mb-2">PDF File *</label>
                  <div className="border-2 border-dashed border-gray-700 rounded-lg p-4 text-center">
                    <input
                      type="file"
                      accept=".pdf"
                      onChange={handleFileChange}
                      className="hidden"
                      id="pdf-upload"
                      data-testid="pdf-file-input"
                    />
                    <label htmlFor="pdf-upload" className="cursor-pointer">
                      {uploadForm.file ? (
                        <div className="text-green-400">
                          <FileText className="w-8 h-8 mx-auto mb-2" />
                          <span>{uploadForm.file.name}</span>
                        </div>
                      ) : (
                        <div className="text-gray-400">
                          <Upload className="w-8 h-8 mx-auto mb-2" />
                          <span>Click to select PDF</span>
                        </div>
                      )}
                    </label>
                  </div>
                </div>

                {/* Submit */}
                <div className="flex gap-3 pt-2">
                  <Button
                    type="button"
                    variant="outline"
                    className="flex-1"
                    onClick={() => setShowUploadForm(false)}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    disabled={uploading}
                    className="flex-1 bg-cyan-600 hover:bg-cyan-700"
                    data-testid="upload-submit-btn"
                  >
                    {uploading ? (
                      <><RefreshCw className="w-4 h-4 mr-2 animate-spin" />Uploading...</>
                    ) : (
                      <><Upload className="w-4 h-4 mr-2" />Upload</>
                    )}
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Replace PDF Modal */}
      <AlertDialog open={replaceModal.open} onOpenChange={(open) => {
        if (!open) {
          setReplaceModal({ open: false, doc: null });
          setReplaceFile(null);
        }
      }}>
        <AlertDialogContent className="bg-[#1a1d23] border-gray-800">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-white">
              Replace PDF: {replaceModal.doc?.title}
            </AlertDialogTitle>
            <AlertDialogDescription className="text-gray-400">
              Upload a new PDF to replace the current file. The public URL will remain unchanged.
              <div className="mt-2 p-2 bg-gray-800 rounded text-xs text-cyan-400">
                Slug: {replaceModal.doc?.slug} (locked)
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          
          <div className="py-4">
            <div className="border-2 border-dashed border-gray-700 rounded-lg p-4 text-center">
              <input
                type="file"
                accept=".pdf"
                onChange={handleReplaceFileChange}
                className="hidden"
                id="replace-pdf-upload"
                data-testid="replace-pdf-input"
              />
              <label htmlFor="replace-pdf-upload" className="cursor-pointer">
                {replaceFile ? (
                  <div className="text-green-400">
                    <FileText className="w-8 h-8 mx-auto mb-2" />
                    <span>{replaceFile.name}</span>
                  </div>
                ) : (
                  <div className="text-gray-400">
                    <Upload className="w-8 h-8 mx-auto mb-2" />
                    <span>Click to select new PDF</span>
                  </div>
                )}
              </label>
            </div>
          </div>
          
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-gray-800 border-gray-700 text-white hover:bg-gray-700">
              Cancel
            </AlertDialogCancel>
            <Button
              onClick={handleReplace}
              disabled={!replaceFile || replacing}
              className="bg-cyan-600 hover:bg-cyan-700"
              data-testid="replace-submit-btn"
            >
              {replacing ? (
                <><RefreshCw className="w-4 h-4 mr-2 animate-spin" />Replacing...</>
              ) : (
                'Replace PDF'
              )}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Documents Grid */}
      {documents.length === 0 ? (
        <Card className="bg-[#1a1d23] border-gray-800">
          <CardContent className="py-12 text-center">
            <FileText className="w-16 h-16 mx-auto mb-4 text-gray-600" />
            <h3 className="text-xl text-white mb-2">No Documents Yet</h3>
            <p className="text-gray-400 mb-4">Upload your first legal document to get started</p>
            <Button onClick={() => setShowUploadForm(true)} className="bg-cyan-600">
              <Upload className="w-4 h-4 mr-2" />
              Upload Document
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4">
          {documents.map(doc => (
            <Card key={doc.id || doc.slug} className="bg-[#1a1d23] border-gray-800" data-testid={`doc-card-${doc.slug}`}>
              <CardContent className="p-4">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <FileText className="w-5 h-5 text-cyan-500" />
                      <h3 className="text-lg font-semibold text-white">{doc.title}</h3>
                      <Badge 
                        variant="outline" 
                        className={doc.visibility === 'public' 
                          ? 'border-green-500/30 text-green-400' 
                          : 'border-yellow-500/30 text-yellow-400'}
                      >
                        {doc.visibility === 'public' ? (
                          <><Globe className="w-3 h-3 mr-1" />Public</>
                        ) : (
                          <><Lock className="w-3 h-3 mr-1" />Private</>
                        )}
                      </Badge>
                    </div>
                    
                    {doc.description && (
                      <p className="text-sm text-gray-400 mb-2">{doc.description}</p>
                    )}
                    
                    {/* Link Targets */}
                    {doc.link_targets && doc.link_targets.length > 0 && (
                      <div className="flex flex-wrap gap-1 mb-2">
                        {doc.link_targets.map(target => (
                          <Badge key={target} variant="outline" className="text-xs border-gray-600">
                            {LINK_TARGETS.find(t => t.value === target)?.label || target}
                          </Badge>
                        ))}
                      </div>
                    )}
                    
                    {/* Public URL */}
                    {doc.visibility === 'public' && doc.public_url && (
                      <div className="flex items-center gap-2 mt-3 p-2 bg-gray-800/50 rounded">
                        <span className="text-xs text-gray-400">Public URL:</span>
                        <code className="text-xs text-cyan-400 flex-1">{window.location.origin}{doc.public_url}</code>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => copyUrl(doc.slug)}
                          className="h-6 px-2"
                        >
                          {copiedSlug === doc.slug ? (
                            <Check className="w-3 h-3 text-green-400" />
                          ) : (
                            <Copy className="w-3 h-3" />
                          )}
                        </Button>
                      </div>
                    )}
                  </div>
                  
                  {/* Actions */}
                  <div className="flex gap-2 ml-4">
                    {doc.visibility === 'public' && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => openPreview(doc.slug)}
                        className="border-gray-700"
                        data-testid={`preview-btn-${doc.slug}`}
                      >
                        <Eye className="w-4 h-4 mr-1" />
                        Preview
                      </Button>
                    )}
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setReplaceModal({ open: true, doc })}
                      className="border-cyan-500/30 text-cyan-400 hover:bg-cyan-500/10"
                      data-testid={`replace-btn-${doc.slug}`}
                    >
                      <RefreshCw className="w-4 h-4 mr-1" />
                      Replace
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleDelete(doc.slug)}
                      className="border-red-500/30 text-red-400 hover:bg-red-500/10"
                      data-testid={`delete-btn-${doc.slug}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Info Card */}
      <Card className="mt-8 bg-blue-500/10 border-blue-500/20">
        <CardContent className="p-4">
          <div className="flex items-start gap-3">
            <FileText className="w-5 h-5 text-blue-400 flex-shrink-0 mt-0.5" />
            <div>
              <h4 className="text-white font-medium mb-1">Persistent Storage</h4>
              <p className="text-sm text-gray-400">
                PDFs are stored in MongoDB GridFS for persistence across redeploy/rollback.
                Public documents are served at <code className="text-cyan-400">/legal/&#123;slug&#125;</code> with 
                read-only PDF viewing. These URLs are suitable for TikTok, Meta, Google, and Stripe compliance reviews.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
